/* allgoodfolk theme — bag and page renderers.
   Prices, variants and stock come from Shopify (window.AGF, written by layout/theme.liquid).
   Photography, print art, hero slides and stories come from agf-data.js (scripts/build_theme.py). */
(() => {
  const A = window.AGF || {};
  const EXTRAS = window.AGF_EXTRAS || {};
  const ROOT = (A.root || "/").replace(/\/?$/, "/");
  const SHOP = ROOT + "collections/all";
  const productUrl = (handle) => ROOT + "products/" + handle;
  const asset = (name) => A.assetBase + name;

  const COLOURS = {
    "Black": "#141414", "White": "#ffffff", "Brown": "#5a3b2e", "Dark Chocolate": "#3b2a24",
    "Sport Grey": "#a3a3a3", "Sand": "#d9c9ab", "Military Green": "#5f6147",
    "Natural": "#efe6d4", "Maroon": "#5b1f2a",
    "Dark Grey Heather": "#3e3e40", "Dark Heather": "#424348",
  };

  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const fmt = new Intl.NumberFormat("en-GB", { style: "currency", currency: A.currency || "GBP" });
  const money = (n) => fmt.format(n);
  const page = document.body.dataset.page;
  const $ = (sel, root = document) => root.querySelector(sel);

  // Theme rasters ship as AVIF with a WebP fallback beside them (scripts/build_theme.py);
  // Shopify's own product photos are served by its CDN in whatever format suits the browser.
  const pic = (src, attrs = "", cls = "", style = "") =>
    `<picture${cls ? ` class="${cls}"` : ""}${style ? ` style="${style}"` : ""}>${/\.webp$/.test(src) ? `<source type="image/avif" srcset="${src.replace(/\.webp$/, ".avif")}">` : ""}<img src="${src}" ${attrs}></picture>`;
  const cdn = (src, w) => (src.startsWith("//") ? "https:" + src : src) + (src.includes("?") ? "&" : "?") + "width=" + w;

  /* ---------- Catalogue: Shopify product + allgoodfolk extras ---------- */
  function normalise(sp) {
    const e = EXTRAS[sp.handle];
    const names = sp.options.map((o) => (typeof o === "string" ? o : o.name));
    const ci = names.findIndex((n) => /^(colour|color)$/i.test(n));
    const si = names.findIndex((n) => /^size$/i.test(n));
    const values = (i) => (i < 0 ? [] : [...new Set(sp.variants.map((v) => v.options[i]))]);
    const colours = values(ci), sizes = values(si);
    const images = e
      ? e.images.map((i) => ({ ...i, src: asset(i.src), thumb: asset(i.thumb) }))
      : (sp.media || []).filter((m) => m.media_type === "image").map((m) => {
          // dropsell labels each photo "<title> — Flat lay, white" or "<title> — Scene 2, black"
          const label = (m.alt || "").split(" — ").pop();
          const colour = colours.find((c) => label.toLowerCase().endsWith(c.toLowerCase())) ?? colours[0] ?? null;
          const worn = /\bworn,? (?:waist[- ]up|full[- ]body)\b|\bworn by a model\b|\bmodel (?:wearing|photo|shot)\b|^scene [123],/i.test(label);
          const framedRoom = /\bshown in a frame\b/i.test(label) && /\broom\b/i.test(label);
          const printDetail = /\b(?:paper|artwork).*detail\b/i.test(label);
          return { src: cdn(m.src, 1400), thumb: cdn(m.src, 720), colour, kind: /flat[- ]lay/i.test(label) ? "flat" : sp.type === "Art Print" ? framedRoom ? "print-room" : printDetail ? "print-detail" : "unknown" : worn ? "worn" : "unknown", alt: sp.type === "Art Print" ? m.alt || sp.title : `${sp.title}${colour ? " in " + colour.toLowerCase() : ""}` };
        });
    return {
      published_at: sp.published_at || e?.published_at, created_at: sp.created_at,
      handle: sp.handle, title: sp.title, price: sp.price / 100, available: sp.available,
      kind: e?.kind || (sp.type === "Art Print" ? "print" : undefined),
      garment: sp.type === "Art Print" ? null : e?.garment || (/hood/i.test(`${sp.type} ${sp.title}`) ? "hoodie" : "tee"),
      colours, sizes, ci, si, variants: sp.variants, images,
      art: e ? asset(e.art) : images[0]?.src || "",
      art_light: Boolean(e?.art_light), hasArt: Boolean(e?.art),
      design_key: e?.design_key || e?.source_design_key || sp.design_key || sp.source_design_key,
      poster: e?.poster || "#62503D",
      story: e?.story || "", scripture: e?.scripture || "", details: e?.details || [], note: e?.note || "",
      description: e ? "" : sp.description || "",
    };
  }
  const retiredHandles = new Set(window.AGF_RETIREMENT?.handles || []);
  const PRODUCTS = (A.products || []).filter(p => !retiredHandles.has(p.handle)).map(normalise);
  if (window.AGF_RETIREMENT?.filmSuspended) document.querySelectorAll("[data-campaign-film]").forEach(film => film.remove());
  // Publication is the release authority; creation is only a native Shopify fallback.
  // Keep the curated order for equal or unavailable dates, without sorting the source array.
  function newestFirst(products) {
    const curated = Object.keys(EXTRAS);
    const date = (p) => [p.published_at, p.created_at].map(Date.parse).find(Number.isFinite) ?? 0;
    const rank = (p, index) => {
      const position = curated.indexOf(p.handle);
      return position < 0 ? curated.length + index : position;
    };
    return products.map((product, index) => ({ product, index }))
      .sort((a, b) => date(b.product) - date(a.product) || rank(a.product, a.index) - rank(b.product, b.index))
      .map(({ product }) => product);
  }
  const kindOf = (p) => p.kind || p.garment;
  // Shop all mixes formats in rounds, so new print batches cannot bury garments.
  // Shuffle copied buckets only; category pages and editorial sequences keep their order.
  function mixedShopOrder(products) {
    const shuffle = (items) => {
      for (let i = items.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        [items[i], items[j]] = [items[j], items[i]];
      }
      return items;
    };
    const buckets = new Map();
    products.forEach((product) => {
      const kind = kindOf(product);
      if (!buckets.has(kind)) buckets.set(kind, []);
      buckets.get(kind).push(product);
    });
    const groups = [...buckets.values()].map(shuffle);
    const mixed = [];
    while (groups.some((group) => group.length)) {
      shuffle(groups.filter((group) => group.length)).forEach((group) => mixed.push(group.pop()));
    }
    return mixed;
  }

  const kindLabel = (p) => kindOf(p) === "print" ? "Prints" : p.garment === "tee" ? "Tees" : "Hoodies";
  const byHandle = Object.fromEntries(PRODUCTS.map((p) => [p.handle, p]));
  const variantFor = (p, colour, size) =>
    p.variants.find((v) => (p.ci < 0 || v.options[p.ci] === colour) && (p.si < 0 || v.options[p.si] === size));

  /* ---------- Chrome ---------- */
  function wireChrome() {
    const type = new URLSearchParams(location.search).get("type") || (page === "shop" ? "shop" : "");
    document.querySelectorAll("[data-nav]").forEach((a) => { if (a.dataset.nav === type) a.setAttribute("aria-current", "page"); });

    const header = $(".site-header");
    const onScroll = () => header.classList.toggle("is-scrolled", scrollY > 8);
    addEventListener("scroll", onScroll, { passive: true }); onScroll();

    const toggle = $(".menu-toggle");
    toggle.addEventListener("click", () => {
      const open = header.classList.toggle("menu-open");
      toggle.setAttribute("aria-expanded", open);
      toggle.textContent = open ? "Close" : "Menu";
    });
    addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !header.classList.contains("menu-open")) return;
      header.classList.remove("menu-open");
      toggle.setAttribute("aria-expanded", "false");
      toggle.textContent = "Menu";
      toggle.focus();
    });

    const guide = $("#size-guide");
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-size-guide]")) { e.preventDefault(); guide.showModal(); }
      if (e.target.closest("[data-size-guide-close]")) guide.close();
      // a click on the backdrop lands on the dialog itself, outside its box
      if (e.target === guide) {
        const r = guide.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) guide.close();
      }
    });

    // Shopify takes the sign-up; this only catches a mistyped address before it's sent
    $(".signup form").addEventListener("submit", (e) => {
      if ($("#signup-email").checkValidity()) return;
      e.preventDefault();
      $(".signup .msg").textContent = "Enter a full email address, like name@example.com.";
    });
  }

  /* ---------- Bag: Shopify's cart ---------- */
  let cart = { items: [], total_price: 0, item_count: 0 };
  let cartBusy = false;
  const cartMessage = (message) => { $("[data-checkout-msg]").textContent = message; };
  const api = (path, body) =>
    fetch(ROOT + path, body
      ? { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) }
      : { headers: { Accept: "application/json" } })
      .then((r) => r.json().then((j) => (r.ok ? j : Promise.reject(j))));

  const refreshCart = () => api("cart.js").then((c) => { cart = c; cartMessage(""); renderBag(); });

  async function addToBag(variant) {
    await api("cart/add.js", { items: [{ id: variant.id, quantity: 1 }] });
    await refreshCart();
    openBag();
  }

  function renderBag() {
    document.querySelectorAll("[data-bag-count]").forEach((el) => (el.textContent = cart.item_count));
    const lines = $(".drawer .lines");
    if (!cart.items.length) {
      lines.innerHTML = `<p class="empty">Your bag is empty. <a href="${SHOP}">Browse the collection</a>.</p>`;
    } else {
      lines.innerHTML = cart.items.map((it) => {
        const p = byHandle[it.handle];
        const opts = (it.options_with_values || []).filter((o) => o.name !== "Title");
        const colour = p && p.ci >= 0 ? opts.find((o) => /colou?r/i.test(o.name))?.value : null;
        const own = p && (p.images.find((im) => im.colour === colour && im.kind === "flat") || null);
        const thumb = own ? own.thumb : it.image ? cdn(it.image, 360) : "";
        return `<div class="line">
          ${thumb ? pic(thumb, 'alt=""') : "<span></span>"}
          <div><a href="${productUrl(it.handle)}"><strong>${esc(it.product_title)}</strong></a>
            <div class="meta">${esc(opts.map((o) => o.value).join(", "))}</div>
            <div class="qty"><button data-key="${esc(it.key)}" data-q="${it.quantity - 1}" aria-label="Remove one">−</button><span>${it.quantity}</span><button data-key="${esc(it.key)}" data-q="${it.quantity + 1}" aria-label="Add one">+</button></div>
          </div>
          <div style="display:grid;justify-items:end;gap:8px"><span>${money(it.final_line_price / 100)}</span><button class="remove" data-key="${esc(it.key)}" data-q="0">Remove</button></div>
        </div>`;
      }).join("");
    }
    $("[data-subtotal]").textContent = money(cart.total_price / 100);
    $("[data-checkout]").disabled = !cart.items.length;
  }

  let bagOpener = null;
  const bagBackground = new Map();
  const bagFocusables = () => [...$(".drawer").querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')]
    .filter((el) => el.getClientRects().length && !el.closest("[hidden], [inert]"));
  function openBag() {
    if (!document.body.classList.contains("bag-open")) bagOpener = document.activeElement;
    const drawer = $(".drawer");
    [...document.body.children].filter((el) => el !== drawer && !el.classList.contains("scrim")).forEach((el) => {
      if (!bagBackground.has(el)) bagBackground.set(el, el.inert);
      el.inert = true;
    });
    drawer.inert = false;
    document.body.classList.add("bag-open");
    drawer.setAttribute("aria-hidden", "false");
    $(".drawer [data-bag-close]").focus();
  }
  function closeBag() {
    document.body.classList.remove("bag-open");
    $(".drawer").inert = true;
    $(".drawer").setAttribute("aria-hidden", "true");
    bagBackground.forEach((inert, el) => { el.inert = inert; });
    bagBackground.clear();
    (bagOpener?.isConnected ? bagOpener : $(".bag-toggle")).focus();
  }

  function wireBag() {
    $(".drawer").inert = true;
    $(".bag-toggle").addEventListener("click", openBag);
    document.querySelectorAll("[data-bag-close]").forEach((b) => b.addEventListener("click", closeBag));
    addEventListener("keydown", (e) => {
      if (!document.body.classList.contains("bag-open")) return;
      if (e.key === "Escape") { e.preventDefault(); closeBag(); }
      if (e.key === "Tab") {
        const controls = bagFocusables(), first = controls[0], last = controls.at(-1);
        if ((e.shiftKey && document.activeElement === first) || (!e.shiftKey && document.activeElement === last)) {
          e.preventDefault(); (e.shiftKey ? last : first)?.focus();
        }
      }
    });
    document.addEventListener("focusin", (e) => {
      if (document.body.classList.contains("bag-open") && !$(".drawer").contains(e.target)) bagFocusables()[0]?.focus();
    });
    $(".drawer .lines").addEventListener("click", (e) => {
      const b = e.target.closest("[data-key]");
      if (!b || cartBusy) return;
      cartBusy = true;
      cartMessage("");
      const controls = [...$(".drawer .lines").querySelectorAll("button")];
      controls.forEach((control) => { control.disabled = true; });
      api("cart/change.js", { id: b.dataset.key, quantity: Number(b.dataset.q) })
        .then((c) => {
          cart = c; renderBag();
          if (document.body.classList.contains("bag-open")) {
            const replacement = [...$(".drawer .lines").querySelectorAll("[data-key]")].find((el) => el.dataset.key === b.dataset.key);
            (replacement || $(".drawer [data-bag-close]")).focus();
          }
        })
        .catch((err) => { cartMessage(err?.description || "Your bag couldn't be updated. Try again."); })
        .finally(() => { cartBusy = false; controls.forEach((control) => { control.disabled = false; }); });
    });
    $("[data-checkout]").addEventListener("click", () => { location.href = ROOT + "checkout"; });
    renderBag();
    refreshCart().then(() => { if (page === "cart") openBag(); }).catch((err) => {
      cartMessage(err?.description || "Your bag couldn't be loaded. Refresh the page to try again.");
      if (page === "cart") openBag();
    });
  }

  /* ---------- Shared pieces ---------- */
  const restingShot = (p) => (p.images || []).find((image) => image.kind === "worn" && (image.thumb || image.src));
  const restingPrintShot = (p) => {
    // A hero-first print leads with its flat lay, composed for the card and shown whole.
    const hero = (p.images || []).find((image) => image.kind === "flat" && image.cardLead === true && (image.thumb || image.src));
    if (hero) return hero;
    const rooms = (p.images || []).filter((image) => image.kind === "print-room" && (image.thumb || image.src));
    return rooms.find((image) => image.cardLead === true) || rooms[0];
  };
  const printCardCropStyle = (image) => {
    const crop = image?.cardCrop;
    if (image?.cardLead !== true || !image.src || !crop) return "";
    const { x, y, side } = crop;
    if (![x, y, side].every(Number.isFinite) || x < 0 || y < 0 || side < .2 || side > 1 || x + side > 1 || y + side > 1) return "";
    return `--print-card-scale:${1 / side};--print-card-x:${-100 * x / side}%;--print-card-y:${-100 * y / side}%`;
  };
  function card(p) {
    const print = kindOf(p) === "print";
    const photo = print ? restingPrintShot(p) : restingShot(p);
    const cropStyle = print ? printCardCropStyle(photo) : "";
    return `<a class="card${print ? " card--print" : ""}" data-handle="${esc(p.handle)}"${print ? "" : ' data-preview="worn"'} href="${productUrl(p.handle)}">
      <div class="card__media">
        ${photo ? pic(cropStyle ? photo.src : photo.thumb || photo.src, `alt="${esc(photo.alt || p.title)}" loading="lazy" width="720" height="720"`, cropStyle ? "print-card-framed" : "", cropStyle) : `<span class="card__placeholder">${print ? "Product" : "Model"} photo unavailable.</span>`}
      </div>
      <div class="card__body">
        <span class="card__title">${esc(String(p.title).split(" | ")[0])}</span><span>${p.available ? money(p.price) : "Sold out"}</span>
        ${!print && p.colours.length > 1 ? `<span class="card__meta">${p.colours.length} colours</span>
        <span class="swatches" aria-label="Colours: ${esc(p.colours.join(", "))}">
          ${p.colours.map((c) => `<i class="swatch" style="background:${COLOURS[c] || "#ccc"}" title="${esc(c)}"></i>`).join("")}
        </span>` : ""}
      </div>
    </a>`;
  }

  function wireCardPreviews() {
    const states = new WeakMap();
    const high = [], low = [];
    let running = 0;
    const pump = () => {
      while (running < 4 && (high.length || low.length)) {
        const { task, done } = (high.length ? high : low).shift();
        running++;
        Promise.resolve().then(task).finally(() => { running--; done(); pump(); });
      }
    };
    const enqueue = (task, priority) => new Promise((done) => {
      (priority ? high : low).push({ task, done });
      pump();
    });
    const stateFor = (card) => {
      let state = states.get(card);
      if (!state) {
        const product = byHandle[card.dataset.handle];
        const worn = product && kindOf(product) !== "print";
        const resting = worn && product ? restingShot(product) : null;
        const shots = (product?.images || []).filter((image) => image.kind === (worn ? "worn" : "flat"));
        const others = shots.filter((image) => image !== resting);
        state = {
          photos: [...new Set((others.length ? others : shots).map((image) => image.thumb || image.src).filter(Boolean))],
          ready: [], current: null, last: null, active: false, warming: false,
        };
        states.set(card, state);
      }
      return state;
    };
    const showReady = (card, state) => {
      if (!state.active || !state.ready.length || !card.isConnected) return;
      const choices = state.ready.length > 1 ? state.ready.filter((photo) => photo.src !== state.last) : state.ready;
      const photo = choices[Math.floor(Math.random() * choices.length)];
      if (state.current) state.current.picture.classList.add("is-off");
      photo.picture.classList.remove("is-off");
      state.current = photo;
      state.last = photo.src;
      card.classList.add("is-preview-ready");
    };
    const decode = async (url) => {
      const image = new Image();
      image.src = url;
      try { await image.decode(); return image; } catch { return null; }
    };
    const warmPhoto = async (card, state, src) => {
      if (!card.isConnected) return;
      const avif = /\.webp$/.test(src) ? src.replace(/\.webp$/, ".avif") : null;
      const decoded = avif ? await decode(avif) : await decode(src);
      const fallback = decoded || (avif ? await decode(src) : null);
      if (!fallback || !card.isConnected) return;
      const picture = document.createElement("picture");
      picture.className = "alt is-off";
      if (decoded && avif) {
        const source = document.createElement("source");
        source.type = "image/avif";
        source.srcset = avif;
        picture.append(source);
      }
      const image = new Image();
      image.alt = "";
      image.width = 720;
      image.height = 720;
      picture.append(image);
      card.querySelector(".card__media").append(picture);
      image.src = src;
      try { await image.decode(); } catch {
        picture.querySelector("source")?.remove();
        try { await image.decode(); } catch { picture.remove(); return; }
      }
      state.ready.push({ src, picture });
      if (state.active && !state.current && (card.matches(":hover") || card.matches(":focus-visible"))) showReady(card, state);
    };
    const warm = (card) => {
      const state = stateFor(card);
      if (state.warming || !state.photos.length) return;
      state.warming = true;
      Promise.all(state.photos.slice(0, 2).map((src) => enqueue(() => warmPhoto(card, state, src), true)))
        .then(() => state.photos.slice(2).forEach((src) => enqueue(() => warmPhoto(card, state, src), false)));
    };
    const start = (card) => {
      const state = stateFor(card);
      state.active = true;
      if (state.current) state.current.picture.classList.add("is-off");
      state.current = null;
      card.classList.remove("is-preview-ready");
      showReady(card, state);
      warm(card);
    };
    const stop = (card) => {
      const state = states.get(card);
      if (state) {
        state.active = false;
        if (state.current) state.current.picture.classList.add("is-off");
        state.current = null;
      }
      card.classList.remove("is-preview-ready");
    };
    if (matchMedia("(hover: hover) and (pointer: fine)").matches && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { observer.unobserve(entry.target); warm(entry.target); }
      }), { rootMargin: "500px" });
      const observe = (root) => {
        if (root.matches?.(".card[data-handle]")) observer.observe(root);
        root.querySelectorAll?.(".card[data-handle]").forEach((card) => observer.observe(card));
      };
      observe(document);
      new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) observe(node);
      }))).observe(document.querySelector("main") || document.body, { childList: true, subtree: true });
    }
    document.addEventListener("pointerover", (event) => {
      if (event.pointerType !== "mouse") return;
      const card = event.target.closest(".card[data-handle]");
      if (card && !card.contains(event.relatedTarget)) start(card);
    });
    document.addEventListener("pointerout", (event) => {
      if (event.pointerType !== "mouse") return;
      const card = event.target.closest(".card[data-handle]");
      if (card && !card.contains(event.relatedTarget) && !card.matches(":focus-visible")) stop(card);
    });
    document.addEventListener("focusin", (event) => {
      const card = event.target.closest(".card[data-handle]");
      if (card && card.matches(":focus-visible") && !card.contains(event.relatedTarget)) start(card);
    });
    document.addEventListener("focusout", (event) => {
      const card = event.target.closest(".card[data-handle]");
      if (card && !card.contains(event.relatedTarget) && !card.matches(":hover")) stop(card);
    });
  }

  function luminance(hex) {
    const n = parseInt(hex.slice(1), 16);
    const [r, g, bl] = [n >> 16, (n >> 8) & 255, n & 255].map((v) => {
      v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  }

  /* ---------- Home: hero carousel ---------- */
  function renderHero() {
    const slides = (window.AGF_HEROES || []).filter((slide) => slide.kind === "print-scene"
      ? ["/collections/art-prints", "/collections/christian-art-prints"].includes(slide.target)
        && [A.collections?.printGeneric, A.collections?.printLegacy, A.collections?.print].includes(slide.target)
        && new Set(slide.product_handles).size >= 2
        && slide.product_handles.every(handle => byHandle[handle] && kindOf(byHandle[handle]) === "print")
      : byHandle[slide.handle]);
    const hero = $(".hero");
    if (!slides.length) return;
    const SLIDE_MS = 6000;
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    hero.style.setProperty("--slide-ms", SLIDE_MS + "ms");

    // Match the cover crop's full image width, including the existing height limits.
    const MOBILE_SIZES = "max(100vw, calc(58svh * 2.5))";
    const HERO_SIZES = `(max-width: 760px) ${MOBILE_SIZES}, max(100vw, calc(max(520px, 40vw, min(1100px, calc(100svh - 97px))) * 2.5))`;
    const set = (sources, ext) => sources.map(([src, w]) => `${asset(src.replace(/\.webp$/, ext))} ${w}w`).join(", ");
    const markup = (s, i) => {
      const mobile = s.mobileSrcset || s.srcset.filter(([, width]) => width <= 2560);
      const mobileSources = mobile.length ? mobile : s.srcset.slice(0, 1);
      return `
      <div class="hero__slide${i === 0 ? " is-active" : ""}${s.kind === "print-scene" ? " hero__slide--print-scene" : ""}" data-hero-handle="${esc(s.handle)}" data-hero-source="${esc(s.srcset[0][0])}" aria-hidden="${i !== 0}">
        <picture>
          <source media="(max-width: 760px)" type="image/avif" srcset="${set(mobileSources, ".avif")}" sizes="${s.kind === "print-scene" ? "100vw" : MOBILE_SIZES}">
          <source type="image/avif" srcset="${set(s.srcset, ".avif")}" sizes="${s.kind === "print-scene" ? "100vw" : HERO_SIZES}">
          <source media="(max-width: 760px)" type="image/webp" srcset="${set(mobileSources, ".webp")}" sizes="${s.kind === "print-scene" ? "100vw" : MOBILE_SIZES}">
          <img src="${asset(s.srcset[0][0])}" srcset="${set(s.srcset, ".webp")}" sizes="${s.kind === "print-scene" ? "100vw" : HERO_SIZES}"
               alt="${esc(s.alt)}" width="${s.width}" height="${s.height}" ${i === 0 ? 'fetchpriority="high" loading="eager"' : 'fetchpriority="low" loading="lazy"'} decoding="async">
        </picture>
      </div>`;
    };
    const holder = $(".hero__slides");
    const first = holder.querySelector(".hero__slide");
    // The generated Liquid picture is discoverable before JS. Keep its live image
    // node and request when it matches this exact current approved first slide.
    if (first?.dataset.heroHandle === slides[0].handle
        && first.dataset.heroSource === slides[0].srcset[0][0] && first.querySelector("img")) {
      holder.insertAdjacentHTML("beforeend", slides.slice(1).map((slide, index) => markup(slide, index + 1)).join(""));
    } else {
      holder.innerHTML = slides.map(markup).join("");
    }
    const dots = $(".hero__dots");
    dots.innerHTML = slides.map((s, i) => `<button aria-label="Show ${esc(s.title)}"${i === 0 ? ' aria-current="true"' : ""}></button>`).join("");
    const now = $(".hero__now");
    const caption = $(".hero__caption");
    const pauseBtn = $(".hero__pause");
    const palette = getComputedStyle(document.documentElement);
    const inkLight = luminance(palette.getPropertyValue("--ink").trim());
    const paperLight = luminance(palette.getPropertyValue("--paper").trim());
    const goldLight = luminance(palette.getPropertyValue("--gold").trim());
    const contrast = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

    let index = 0, timer = null, paused = reduce, focused = false;
    const show = (i) => {
      index = (i + slides.length) % slides.length;
      const s = slides[index];
      hero.querySelectorAll(".hero__slide").forEach((el, k) => {
        el.classList.toggle("is-active", k === index);
        el.setAttribute("aria-hidden", k !== index);
      });
      dots.querySelectorAll("button").forEach((b, k) => b.setAttribute("aria-current", k === index));
      // restart the progress bar on the new dot
      hero.classList.remove("is-playing"); void hero.offsetWidth; hero.classList.toggle("is-playing", !paused && !focused);
      hero.style.background = s.backdrop;
      // Pick the more legible small type; keep the mark Earth or Gold where it reads clearly.
      const backdropLight = luminance(s.backdrop);
      const useInk = contrast(backdropLight, inkLight) >= contrast(backdropLight, paperLight);
      const typeColour = useInk ? "var(--ink)" : "var(--paper)";
      const typeContrast = contrast(backdropLight, useInk ? inkLight : paperLight);
      hero.style.setProperty("--hero-ink", typeColour);
      hero.style.setProperty("--hero-mark", backdropLight > 0.55 ? "var(--earth)" : contrast(backdropLight, goldLight) >= 3 ? "var(--gold)" : typeColour);
      hero.style.setProperty("--hero-halo", typeContrast < 4.5 ? (useInk ? "var(--paper)" : "var(--ink)") : "transparent");
      now.textContent = s.kind === "print-scene" ? "Shop prints ↗" : "Shop this piece ↗";
      now.href = s.kind === "print-scene" ? s.target : productUrl(s.handle);
      if (caption) caption.textContent = s.title;
      schedule();
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!paused && !focused) timer = setTimeout(() => show(index + 1), SLIDE_MS);
    };
    const setPaused = (p) => {
      paused = p;
      hero.classList.toggle("is-paused", p || focused);
      hero.classList.toggle("is-playing", !p && !focused);
      pauseBtn.textContent = p ? "Play" : "Pause";
      pauseBtn.setAttribute("aria-pressed", p);
      schedule();
    };

    dots.addEventListener("click", (e) => {
      const b = e.target.closest("button");
      if (b) show([...dots.children].indexOf(b));
    });
    pauseBtn.addEventListener("click", () => setPaused(!paused));
    hero.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") show(index + 1);
      if (e.key === "ArrowLeft") show(index - 1);
    });
    // swipe on touch screens
    let x0 = null;
    hero.addEventListener("pointerdown", (e) => { if (e.pointerType !== "mouse") x0 = e.clientX; });
    hero.addEventListener("pointerup", (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0; x0 = null;
      if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
    });
    // hold still while someone is reading or using the controls
    hero.addEventListener("focusin", () => { focused = true; setPaused(paused); });
    hero.addEventListener("focusout", (e) => {
      if (hero.contains(e.relatedTarget)) return;
      focused = false; setPaused(paused);
    });

    setPaused(paused);
    show(0);
    requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add("is-ready")));
  }

  /* ---------- Home ---------- */
  function renderArtworkDrawers() {
    const list = $("#stories .artwork-drawers");
    if (!list) return;
    const nameOf = (p) => String(p.name || p.title).split(" | ")[0].replace(/\s+(?:tee|t[ -]?shirt|hoodie|art print|poster)$/i, "").trim();
    const canonical = (text) => String(text || "").trim().replace(/\s+/g, " ");
    const designs = new Map();
    PRODUCTS.forEach((p) => {
      const name = nameOf(p), meaning = canonical(p.story);
      const placeholder = meaning.replace(/[.!?]+$/, "").replace(/\s+(?:tee|t[ -]?shirt|hoodie|art print|poster)$/i, "").toLowerCase();
      if (!p.art || p.hasArt === false) return;
      const validMeaning = placeholder === name.toLowerCase() ? "" : meaning;
      const key = String(p.design_key || p.source_design_key || JSON.stringify([p.design || name, name, validMeaning, canonical(p.scripture)]));
      if (!designs.has(key)) designs.set(key, { key, name, meaning: validMeaning ? p.story : "", product: p, products: [] });
      const design = designs.get(key);
      if (!design.products.some((item) => item.handle === p.handle)) design.products.push(p);
    });
    list.innerHTML = [...designs.values()].map(({ key, name, meaning, product: p, products }) => `
      <details class="artwork-drawer" data-design-key="${esc(key)}">
        <summary><span class="artwork-drawer__art"${p.art_light ? ' style="--artwork-paper:var(--ink)"' : ""}>${pic(p.art, `alt="${esc(name)} artwork" loading="lazy"`)}</span><span class="artwork-drawer__label">${esc(name)} <span aria-hidden="true">+</span></span></summary>
        <div class="artwork-drawer__meaning"><h3>${esc(name)}</h3><p>${esc(meaning)}</p>${p.scripture ? `<span class="ref">${esc(p.scripture)}</span>` : ""}
          <div class="artwork-drawer__links">${products.map((item) => `<a href="${productUrl(item.handle)}">${kindOf(item) === "print" ? "Art print" : item.garment === "hoodie" ? "Hoodie" : "T-shirt"}</a>`).join("")}</div>
        </div>
      </details>`).join("");
    document.dispatchEvent(new Event("agf:artwork-render"));
  }

  function renderHome() {
    renderHero();
    renderReel();

    const grid = $("#collection .grid");
    const controls = $("#collection [data-home-categories]");
    const fallback = $("#collection [data-home-category-links]");
    const empty = $("#collection [data-home-empty]");
    const film = grid.querySelector(":scope > [data-campaign-film]");
    const drawCollection = (type) => {
      const products = newestFirst(PRODUCTS.filter((p) => kindOf(p) === type));
      // Only cards are refreshed; the same film retains its playback state and listeners.
      grid.querySelectorAll(":scope > .card").forEach((node) => node.remove());
      grid.insertAdjacentHTML("beforeend", products.map((p) => card(p, true)).join(""));
      if (film) {
        film.hidden = type === "print";
        film.dispatchEvent(new Event("agf:film-visibility"));
      }
      const label = type === "tee" ? "T-shirts" : type === "hoodie" ? "Hoodies" : "Prints";
      grid.setAttribute("aria-label", label);
      if (empty) {
        empty.textContent = products.length ? "" : `No ${label.toLowerCase()} are available yet.`;
        empty.hidden = products.length > 0;
      }
      controls.dataset.type = type;
      controls.querySelectorAll("[data-home-kind]").forEach((button) => {
        button.setAttribute("aria-pressed", String(button.dataset.homeKind === type));
      });
    };
    // An incomplete feed keeps native cards and category links instead of a partial switch.
    if (controls && A.catalogueComplete !== false) {
      drawCollection(["tee", "hoodie", "print"].includes(controls.dataset.type) ? controls.dataset.type : "tee");
      // Replacing our one handler avoids duplicate listeners if the catalogue is refreshed.
      controls.onclick = (event) => {
        const button = event.target.closest("[data-home-kind]");
        if (button && controls.contains(button)) drawCollection(button.dataset.homeKind);
      };
      controls.hidden = false;
      if (fallback) fallback.hidden = true;
    }

    renderArtworkDrawers();
  }

  /* ---------- Home: ordered square lifestyle strip ---------- */
  function renderReel() {
    const section = $(".reel");
    const track = $(".reel__track");
    const shots = window.AGF_LIFESTYLE || [];
    if (!section || !track) return;
    track._reelCleanup?.();
    track.innerHTML = shots.map(s => {
      const p = byHandle[s.handle];
      const title = (p ? p.title : s.handle.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())).split(" | ")[0];
      const body = `${pic(asset(s.src), `alt="${esc(`${title} worn out with friends`)}" loading="lazy" width="${s.width}" height="${s.height}"` )}`;
      return p ? `<a class="reel__photo" href="${productUrl(p.handle)}" aria-label="${esc(`Shop ${title}`)}">${body}</a>`
        : `<figure class="reel__photo">${body}</figure>`;
    }).join("");
    window.AGF_LIFESTYLE_STRIP?.mount(section, track);
  }

  /* ---------- Home: stacked sections ----------
     Each section pins once its bottom is on screen and the next slides over it.
     --pin is the sticky offset (negative for sections taller than the viewport);
     --cover (0 to 1) darkens a section as the next one covers it. */
  function stackPanels() {
    const panels = [...document.querySelectorAll(".stack")];
    if (!panels.length || !matchMedia("(prefers-reduced-motion: no-preference)").matches) return;
    const headerH = () => $(".site-header").offsetHeight;
    const pin = () => panels.forEach((el) => {
      el.style.setProperty("--pin", `${Math.min(headerH(), innerHeight - el.offsetHeight)}px`);
    });
    let queued = false;
    const cover = () => {
      queued = false;
      const span = innerHeight - headerH();
      panels.forEach((el, i) => {
        const next = panels[i + 1];
        if (!next) return;
        const c = Math.min(1, Math.max(0, (innerHeight - next.getBoundingClientRect().top) / span));
        el.style.setProperty("--cover", c.toFixed(3));
      });
    };
    const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(cover); } };
    new ResizeObserver(() => { pin(); onScroll(); }).observe(document.body);
    addEventListener("scroll", onScroll, { passive: true });
    addEventListener("resize", pin);
    pin(); cover();

    // keyboard focus never lands under the section sliding over it
    document.addEventListener("focusin", (e) => {
      const panel = e.target.closest?.(".stack");
      // A normal-flow gallery can cover a sticky panel too.
      const next = panel && panel.nextElementSibling;
      if (!next) return;
      const gap = next.getBoundingClientRect().top - e.target.getBoundingClientRect().bottom - 16;
      if (gap < 0) scrollBy(0, gap);
    });
  }

  /* ---------- Shop (collection) ---------- */
  function renderShop() {
    // Keep Shopify's native 24-item pagination if this 250-item feed is incomplete.
    if (A.catalogueComplete === false) return;
    const nativePagination = $(".pagination");
    if (nativePagination) nativePagination.hidden = true;
    const requestedType = new URLSearchParams(location.search).get("type");
    const type = ["tee", "hoodie", "print"].includes(requestedType) ? requestedType : null;
    const inCollection = A.collection ? PRODUCTS.filter((p) => A.collection.includes(p.handle)) : PRODUCTS;
    const selected = inCollection.filter((p) => !type || kindOf(p) === type);
    const isShopAll = !type && location.pathname.replace(/\/$/, "") === SHOP.replace(/\/$/, "");
    const list = isShopAll ? mixedShopOrder(selected) : newestFirst(selected);
    if (type) {
      const title = type === "tee" ? "Tees" : type === "hoodie" ? "Hoodies" : type === "print" ? "Prints" : "Shop all";
      document.title = `${title} | allgoodfolk`;
      $(".shop-head h1").textContent = title;
    }
    const nativeType = ["tee", "hoodie", "print"].find((t) => A.collections?.[t] === location.pathname)
      || ([A.collections?.printGeneric, A.collections?.printLegacy].includes(location.pathname) ? "print" : "");
    $(".filters").innerHTML = [["", "All"], ["tee", "Tees"], ["hoodie", "Hoodies"], ["print", "Prints"]]
      .map(([t, l]) => `<a href="${t ? A.collections?.[t] || `${SHOP}?type=${t}` : SHOP}"${(type || nativeType) === t ? ' aria-current="true"' : ""}>${l}</a>`).join("")
      + `<span class="count">${list.length} pieces</span>`;
    $(".grid").innerHTML = list.map((p) => card(p, true)).join("");
  }

  /* ---------- Product ---------- */
  function renderProduct() {
    const p = byHandle[A.handle];
    if (!p) return; // not in the catalogue feed: the server-rendered title stays
    const { colours, sizes } = p;
    const variantId = new URLSearchParams(location.search).get("variant");
    const linkedVariant = /^\d+$/.test(variantId || "")
      ? p.variants.find((variant) => String(variant.id) === variantId) : undefined;
    let colour = linkedVariant && p.ci >= 0 ? linkedVariant.options[p.ci]
      : p.images.find((i) => colours.includes(i.colour))?.colour ?? colours[0] ?? null;
    let size = sizes.length ? linkedVariant ? linkedVariant.options[p.si] : null : undefined;

    const gallery = $(".gallery");
    const drawGallery = () => {
      const ordered = [...p.images.filter((i) => i.colour === colour), ...p.images.filter((i) => i.colour !== colour)];
      gallery.innerHTML = ordered.map((im, i) => `<figure>${pic(im.src, `alt="${esc(im.alt)}" width="1400" height="1400" ${i > 1 ? 'loading="lazy"' : ""}`)}</figure>`).join("");
      gallery.scrollLeft = 0;
    };

    $(".buy").innerHTML = `
      <nav class="crumbs" aria-label="Breadcrumb"><a href="${SHOP}">Shop</a> / <a href="${A.collections?.[kindOf(p)] || `${SHOP}?type=${kindOf(p)}`}">${kindLabel(p)}</a></nav>
      <div><h1 class="display">${esc(p.title)}</h1><div class="price">${money(p.price)}</div></div>
      ${colours.length ? `<fieldset class="option"><legend>Colour: <span data-colour-name>${esc(colour)}</span></legend>
        <div class="chips">${colours.map((c) => `<button class="chip chip--colour" data-colour="${esc(c)}" aria-pressed="${c === colour}" aria-label="${esc(c)}" title="${esc(c)}"><i style="background:${COLOURS[c] || "#ccc"}"></i></button>`).join("")}</div>
      </fieldset>` : ""}
      ${sizes.length ? `<fieldset class="option"><legend>Size ${kindOf(p) !== "print" ? '<a class="size-link" href="#size-guide" data-size-guide>Size guide</a>' : ""}</legend>
        <div class="chips">${sizes.map((s) => `<button class="chip" data-size="${esc(s)}" aria-pressed="${s === size}">${esc(s)}</button>`).join("")}</div>
      </fieldset>` : ""}
      <button class="btn btn--block" data-add>Add to bag</button>
      <p class="hint" role="status" aria-live="polite"></p>
      <ul class="assure">
        <li>Printed to order, just for you</li>
        <li>Delivery options and costs at checkout</li>
        <li>Secure checkout</li>
      </ul>
      <div class="accordion">
        ${p.story ? `<details open><summary>The story</summary><div class="panel">
          <p>${esc(p.story)}</p>${p.scripture ? `<p>${esc(p.scripture)}</p>` : ""}
          ${pic(p.art, `alt="${esc(p.title)} print artwork"`, "story-art")}
        </div></details>` : ""}
        ${p.details.length ? `<details><summary>Details</summary><div class="panel">
          <ul>${p.details.map((d) => `<li>${esc(d)}</li>`).join("")}</ul>
        </div></details>` : p.description ? `<details open><summary>Details</summary><div class="panel">${p.description}</div></details>` : ""}
        <details><summary>Shipping</summary><div class="panel">
          <p>Every piece is printed to order. Available destinations, delivery options and costs are shown at checkout.</p>
        </div></details>
        ${p.note ? `<details><summary>How it's made</summary><div class="panel"><p>${esc(p.note)}</p></div></details>` : ""}
      </div>`;

    const hint = $(".buy .hint");
    const addBtn = $("[data-add]");

    // Mobile buy bar: keeps Add to bag in reach once the main button is off screen.
    // It drives the main button, so size checks and messages stay in one place.
    const bar = document.createElement("div");
    bar.className = "buybar";
    bar.innerHTML = `<div class="buybar__info"><strong>${esc(p.title)}</strong><span data-bar-price></span></div><button class="btn" data-bar-add>Add to bag</button>`;
    document.body.append(bar);
    const barPrice = $("[data-bar-price]", bar);
    const barBtn = $("[data-bar-add]", bar);
    barBtn.addEventListener("click", () => {
      addBtn.click();
      if (size === null) {
        const sizes = $(".buy [data-size]")?.closest("fieldset");
        sizes?.scrollIntoView({ block: "center", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
        $(".buy [data-size]:not([disabled])")?.focus({ preventScroll: true });
      }
    });
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(([entry]) => bar.classList.toggle("is-visible", !entry.isIntersecting)).observe(addBtn);
    }
    let adding = false;
    const updateSelection = (updateUrl = false) => {
      const variant = variantFor(p, colour, size);
      $(".buy .price").textContent = money(variant ? variant.price / 100 : p.price);
      $(".buy").querySelectorAll("[data-size]").forEach((button) => {
        button.disabled = !variantFor(p, colour, button.dataset.size)?.available;
      });
      const colourAvailable = p.variants.some((v) => v.available && (p.ci < 0 || v.options[p.ci] === colour));
      const soldOut = !colourAvailable || (size !== null && !variant?.available);
      addBtn.disabled = adding || soldOut;
      addBtn.textContent = soldOut ? "Sold out" : "Add to bag";
      barPrice.textContent = $(".buy .price").textContent;
      barBtn.disabled = addBtn.disabled;
      barBtn.textContent = addBtn.textContent;
      if (soldOut) hint.textContent = "That one's sold out. Try another size or colour.";
      if (updateUrl) {
        const url = new URL(location.href);
        if (variant) url.searchParams.set("variant", String(variant.id));
        else url.searchParams.delete("variant");
        if (/^https?:$/.test(url.protocol) && url.href !== location.href) {
          history.replaceState(history.state, "", url.href);
        }
      }
    };
    $(".buy").addEventListener("click", (e) => {
      const c = e.target.closest("[data-colour]"), s = e.target.closest("[data-size]");
      if (c) {
        colour = c.dataset.colour;
        document.querySelectorAll("[data-colour]").forEach((b) => b.setAttribute("aria-pressed", b === c));
        $("[data-colour-name]").textContent = colour;
        drawGallery();
        hint.textContent = "";
        updateSelection(true);
      }
      if (s) {
        size = s.dataset.size;
        document.querySelectorAll("[data-size]").forEach((b) => b.setAttribute("aria-pressed", b === s));
        hint.textContent = "";
        updateSelection(true);
      }
      if (e.target.closest("[data-add]")) {
        if (size === null) { hint.textContent = "Choose a size to add this to your bag."; return; }
        const v = variantFor(p, colour, size);
        if (!v || !v.available) { hint.textContent = "That one's sold out. Try another size or colour."; return; }
        adding = true; updateSelection();
        addToBag(v)
          .catch((err) => { hint.textContent = err?.description || "That didn't go into your bag. Try again."; })
          .finally(() => { adding = false; updateSelection(); });
      }
    });
    drawGallery();
    updateSelection();

    const moreGrid = $(".more .grid");
    if (moreGrid) moreGrid.innerHTML = PRODUCTS.filter((x) => x !== p && kindOf(x) === kindOf(p)).slice(0, 4).map((p) => card(p, false)).join("");
  }

  wireChrome();
  wireBag();
  if (page === "home") { renderHome(); stackPanels(); }
  if (page === "shop") renderShop();
  if (page === "product") renderProduct();
  wireCardPreviews();
})();
